import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/client';

export interface IFriendItem {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  friendsSince: string;
  roomId: string | null;
}

export interface IBlockItem {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  blockedAt: string;
}

export interface IFriendRequestItem {
  id: string;
  requester: {
    id: string;
    username: string;
    displayName: string;
  };
  addressee: {
    id: string;
    username: string;
    displayName: string;
  };
  status: 'pending' | 'accepted' | 'declined';
  createdAt: string;
  acceptedAt: string | null;
}

export interface IPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

export const friendKeys = {
  all: ['friends'] as const,
  userFriends: (userId: string, page: number) =>
    [...friendKeys.all, 'user', userId, 'friends', page] as const,
  userBlocks: (userId: string, page: number) =>
    [...friendKeys.all, 'user', userId, 'blocks', page] as const,
  userRequests: (userId: string, page: number) =>
    [...friendKeys.all, 'user', userId, 'requests', page] as const,
};

export function useUserFriends(userId: string, page = 1, limit = 20) {
  return useQuery({
    queryKey: friendKeys.userFriends(userId, page),
    queryFn: () =>
      api.get<IPaginatedResponse<IFriendItem>>(
        `/backoffice/users/${userId}/friends?page=${page}&limit=${limit}`,
      ),
    enabled: !!userId,
    placeholderData: (previousData) => previousData,
  });
}

export function useUserBlocks(userId: string, page = 1, limit = 20) {
  return useQuery({
    queryKey: friendKeys.userBlocks(userId, page),
    queryFn: () =>
      api.get<IPaginatedResponse<IBlockItem>>(
        `/backoffice/users/${userId}/blocks?page=${page}&limit=${limit}`,
      ),
    enabled: !!userId,
    placeholderData: (previousData) => previousData,
  });
}

export function useUserFriendRequests(userId: string, page = 1, limit = 20) {
  return useQuery({
    queryKey: friendKeys.userRequests(userId, page),
    queryFn: () =>
      api.get<IPaginatedResponse<IFriendRequestItem>>(
        `/backoffice/users/${userId}/friend-requests?page=${page}&limit=${limit}`,
      ),
    enabled: !!userId,
  });
}
